import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import React, { useContext } from 'react';

import { getSelfPayInsuranceFields } from '@/components/InsuranceSection/utils/getSelfPayInsuranceFields';
import InPlaceEditor from '@/components/PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import { RecordType } from '@/components/PersonInPlaceEditor/components/InPlaceEditor/types';
import AppContext from '@/contexts/appContext';
import useInsurancePolicyFormFields from '@/hooks/useInsurancePolicyFormFields';
import { insurancePolicySummariesQueryKey } from '@/hooks/useInsurancePolicySummaries';
import PersonContext from '@/pages/Person/PersonContext';
import { insuranceSectionSchema } from '@/pages/Profile/schemas';
import InsuranceRepository from '@/repositories/InsuranceRepository';
import { InsurancePolicy as InsurancePolicyInterface } from '@/types/insurancePolicy';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import './styles.css';
import type { Props } from './types';

const PolicyEditor = ({ policy, onClose }: Props) => {
  const insuranceRepository = InsuranceRepository();
  const queryClient = useQueryClient();
  const { addMessage } = useContext(AppContext);
  const {
    person: { id: personId },
    refetch: refetchPerson
  } = useContext(PersonContext);

  const { buildFields, submittedValuesManipulator } = useInsurancePolicyFormFields({
    includeVerificationFields: false
  });

  const mutateInsurancePolicy = useMutation({
    mutationFn: async ({
      id,
      values
    }: {
      id?: string;
      values: Partial<InsurancePolicyInterface>;
    }) => {
      if (!Object.keys(values).length) {
        return;
      }

      if (!id) {
        return await insuranceRepository.createInsurancePolicy(values);
      }

      return await insuranceRepository.updateInsurancePolicy(id, values);
    },
    onSuccess: async (response) => {
      if (!response) return;
      queryClient.invalidateQueries({ queryKey: insurancePolicySummariesQueryKey(personId) });
      await refetchPerson();
      addMessage('success', 'Insurance Policy saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const fields = buildFields({
    personIdField: !policy,
    insuranceCardImages: policy?.insurance_card_images
  });

  const record = policy
    ? ({
        ...policy,
        co_insurance: policy.co_insurance ? policy.co_insurance * 100 : null,
        co_pay: policy.co_pay ? policy.co_pay / 100 : null,
        out_of_pocket_maximum: policy.out_of_pocket_maximum
          ? policy.out_of_pocket_maximum / 100
          : null
      } as InsurancePolicyInterface)
    : ({ id: undefined, carrier: null, person_id: personId } as InsurancePolicyInterface);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5 rounded-md border border-uniform-gray-200 bg-uniform-gray-50 px-3 py-2.5 text-sm text-uniform-gray-700">
        <FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4 shrink-0 text-uniform-gray-500" />
        <span>
          Adding a verification? Use <strong>Verify</strong> on the policy card.
        </span>
      </div>
      <InPlaceEditor
        fields={policy?.is_self_pay ? getSelfPayInsuranceFields(fields) : fields}
        mutation={mutateInsurancePolicy}
        newRecord={!policy}
        record={record}
        recordType={RecordType.InsurancePolicy}
        schema={insuranceSectionSchema}
        submittedValuesManipulator={submittedValuesManipulator}
        initialIsEditing
        onEditingChange={(isEditing) => {
          if (!isEditing) onClose();
        }}
      />
    </div>
  );
};

export default React.memo(PolicyEditor);
